import React, { FC, useState, useEffect } from "react";
import styled from "styled-components";
import contentfulComponentLoop from "../lib/contentfulComponentLoop";
import MultiTab, { MultiTabProps } from "../components/MultiTab";
import { Entry } from "contentful";

interface MultiTabContainerProps extends Entry {
  id: string;
  brand: string;
  parentProps: any;
  fields: {
    tabs: [MultiTabProps];
    tabContent: [Entry];
    tabLayout: string;
    tabStyle?: string;
  };
}

const RootContainer = styled.div<{ $forceFullWidth: boolean }>`
  overflow: ${({ $forceFullWidth }) => ($forceFullWidth ? 'visible' : 'hidden')};
`;

const TabContainer = styled.div<{ $layout: string }>`
  overflow: scroll;
  display: flex;
  margin-bottom: 32px;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;

  ${({ $layout }) => $layout == 'center' && `
    @media (min-width: 768px) {
      justify-content: center;
    }
  `}

  ${({ $layout }) => $layout == 'right' && `
    @media (min-width: 768px) {
      justify-content: flex-end;
    }
  `}
`;

const ContentContainer = styled.div<{ $activeIndex: number, $isCarousel: boolean }>`
  ${({ $activeIndex, $isCarousel }) => `
    > * {
      display: none;

      &:nth-child(${$activeIndex + 1}) {
        display: ${$isCarousel ? 'inline-block' : 'flex'};
      }
    }
  `}
`;

const MultiTabContainer: FC<MultiTabContainerProps> = ({ id, brand, parentProps, fields: { 
  tabContent, 
  tabLayout, 
  tabs, 
  tabStyle = 'Underlined' 
}}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [forceFullWidth, setForceFullWidth] = useState(false);

  // Callback to update forceFullWidth state based on Carousel content
  const handleForceFullWidthChange = (value: boolean) => {
    if (value !== forceFullWidth) {
      setForceFullWidth(value);
    }
  };

  useEffect(() => {
    // Check if any of the tabContent contains a carousel with forceFullWidth true
    const containsForceFullWidth = tabContent.some((content) => {
      const { fields } = content as any;
      return fields.forceFullWidth === true;
    });

    if (containsForceFullWidth) {
      setForceFullWidth(true);
    }
  }, [tabContent]);

  const isCarousel = tabContent[tabIndex]?.sys.contentType.sys.id === 'carousel';

  return (
    <RootContainer className="multi-tab-root-component" data-component-id={id} $forceFullWidth={forceFullWidth}>
      <TabContainer $layout={tabLayout}>
        {tabs.map((tab, index) => (
          <MultiTab
            {...tab}
            brand={brand}
            theme={parentProps.theme}
            key={tab.sys.id}
            tabStyle={tabStyle}
            isActive={tabIndex == index}
            id={tab.sys.contentType.sys.id}
            onClick={() => setTabIndex(index)}
          />
        ))}
      </TabContainer>
      <ContentContainer $activeIndex={tabIndex} $isCarousel={isCarousel}>
        {contentfulComponentLoop(tabContent, {
          onForceFullWidthChange: handleForceFullWidthChange,
        })}
      </ContentContainer>
    </RootContainer>
  );
};

export default MultiTabContainer;